import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import {
  FileText,
  Plus,
  Search,
  Pencil,
  Trash2,
  Eye,
  ReceiptText,
  X,
  MessageCircle,
  Loader,
  Link2,
  SearchX,
  Sparkles,
  ArrowLeft,
} from "lucide-react";
import MarketplaceEmptyState, { MarketplaceLoadingSpinner } from "../Marketplace/MarketplaceEmptyState";
import PaymentLinkModal from "./components/PaymentLinkModal";
import { MenuContainer, ThreeDotsButton, Dot, DropdownMenu, MenuItem, ItemIconContainer, ItemLabel, Backdrop, ToolbarLeft, ToolbarRight } from "../documentSharedStyles";
import {
  PageContainer,
  Hero,
  HeroGlow,
  HeroContent,
  HeroLeft,
  BackChip,
  HeroText,
  HeroBadge,
  PageTitle,
  PageSubtitle,
  Panel,
  Toolbar,
  SearchControl,
  ClearButton,
  EntriesControl,
  FooterRow,
  FooterText,
  Pagination,
  PageButton,
  type MenuCoords,
} from "../quotationMasterListShared";
import ExportDropdown from "../../../atoms/Dashboard/ExportDropdown";
import { SubmitButton } from "../../../molecules/Dashboard/Member/memberFormStyles";
import { showSuccessToast, showErrorToast } from "../../../atoms/Utlis/Toast";
import { useAppDispatch, useAppSelector } from "../../../../Redux/Hooks";
import { getAllInvoicesAPI, deleteInvoiceAPI, getInvoiceByIdAPI, shareInvoiceWhatsAppAPI } from "../../../../Redux/ApiCalls/Dashboard/InvoiceAPI";
import DeletePopup from "../../../atoms/Dashboard/Folder/DeletePopup";
import { copyDataToClipboard, exportDataToCSV, exportDataToExcel } from "../../../../Utils/exportHelpers";
import { exportElementAsPDF } from "../../../../Utils/pdfHelpers";
import { DocumentPrintTemplate } from "../DocumentPrintTemplate";
import { ListReportPrintTemplate, ReportStatusBadge, CellBold } from "../ListReportPrintTemplate";
import InvoiceShareModal from "./components/InvoiceShareModal";
import StudioTermsManageModal from "../StudioTermsManageModal";
import { INVOICE_STATUS_LABELS, PAYMENT_STATUS_LABELS } from "../../../../Data/invoice.dto";
import { formatINR } from '../../../../Utils/helper';
import {
  PanelTableScroll,
  TableWrap,
  PaymentsTable,
  AmountCell,
  RemainingCell,
  ListHeader,
  ListTitle,
  CountBadge,
} from "../../../atoms/Dashboard/CustomerTransaction/transactionTableStyles";

import PermissionGate from "../../../atoms/Utlis/PermissionGate";
import { usePermissions } from "../../../../hooks/usePermissions";

const ACTION_BUTTONS = [
  { key: "view", icon: Eye, label: "View" },
  { key: "edit", icon: Pencil, label: "Edit" },
  { key: "payment", icon: ReceiptText, label: "Payments" },
  { key: "paymentLink", icon: Link2, label: "Share Payment Link" },
  { key: "share", icon: MessageCircle, label: "Share Invoice" },
  { key: "delete", icon: Trash2, label: "Delete" },
] as const;

const InvoicesPage = styled(PageContainer)`
  height: auto;
  min-height: 100%;
  overflow: visible;
  flex-shrink: 0;
`;

const InvoicesPanel = styled(Panel)`
  flex: none;
  min-height: unset;
  overflow: visible;
`;

const InvoicesTableScroll = styled(PanelTableScroll)`
  flex: none;
  overflow-y: visible;
  min-height: unset;
`;

const AllInvoicesPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { hasPermission, hasAnyPermission } = usePermissions();
  const { allInvoices, totalCount, loading } = useAppSelector((state) => state.invoice);

  const allowedActionButtons = useMemo(() => {
    return ACTION_BUTTONS.filter((btn) => {
      if (btn.key === "delete") return hasAnyPermission(["invoice.delete", "payments_management.delete"]);
      if (btn.key === "edit") return hasAnyPermission(["invoice.update", "payments_management.update"]);
      if (btn.key === "paymentLink") return hasAnyPermission(["invoice.create", "invoice.update", "payments_management.create", "payments_management.update"]);
      if (btn.key === "payment") return hasAnyPermission(["invoice.read", "payments_management.read"]);
      if (btn.key === "share") return hasAnyPermission(["invoice.read", "payments_management.read"]);
      if (btn.key === "view") return hasAnyPermission(["invoice.read", "payments_management.read"]);
      return true;
    });
  }, [hasAnyPermission]);

  const [searchVal, setSearchVal] = useState("");
  const [searchText, setSearchText] = useState("");

  const [entries, setEntries] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string | number; no: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [activeMenuId, setActiveMenuId] = useState<string | number | null>(null);
  const [menuCoords, setMenuCoords] = useState<MenuCoords | null>(null);
  const menuButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const scrollRef = useRef<HTMLDivElement>(null);

  const { user, studio_logo, business, selectedBranch } = useAppSelector((state) => state.auth);
  const [isStudioTermsModalOpen, setIsStudioTermsModalOpen] = useState(false);
  const hasStudioTerms = Boolean(business?.terms_and_conditions && business.terms_and_conditions.trim());
  const [sharingInvoiceId, setSharingInvoiceId] = useState<string | number | null>(null);
  const [modalShareInvoice, setModalShareInvoice] = useState<any>(null);
  const [activeInvoiceForShare, setActiveInvoiceForShare] = useState<any>(null);
  const sharePrintRef = useRef<HTMLDivElement>(null);
  const reportPrintRef = useRef<HTMLDivElement>(null);

  const [paymentLinkInvoiceId, setPaymentLinkInvoiceId] = useState<number | null>(null);
  const [paymentLinkInstallment, setPaymentLinkInstallment] = useState<any | null>(null);
  const [fetchingInvoiceForLink, setFetchingInvoiceForLink] = useState<string | number | null>(null);

  const handleSharePaymentLink = async (inv: any) => {
    setFetchingInvoiceForLink(inv.id);
    try {
      const fullInvoice = await getInvoiceByIdAPI(inv.id);
      if (!fullInvoice.installments || fullInvoice.installments.length === 0) {
        showErrorToast("This invoice has no installments.");
        return;
      }
      const unpaid = fullInvoice.installments.find((inst: any) => !(inst.paid || inst.status === "paid"));
      if (!unpaid) {
        showSuccessToast("All installments for this invoice are already paid.");
        return;
      }
      setPaymentLinkInvoiceId(Number(inv.id));
      setPaymentLinkInstallment(unpaid);
    } catch (err: any) {
      const responseData = err?.response?.data;
      const errorMsg = responseData?.message || responseData?.error?.message || err?.message || "Failed to fetch invoice details.";
      showErrorToast(errorMsg);
    } finally {
      setFetchingInvoiceForLink(null);
    }
  };

  const handleShareInvoice = async (invoiceId: string | number) => {
    try {
      const fullInvoice = await getInvoiceByIdAPI(invoiceId);
      if (!fullInvoice) {
        showErrorToast("Invoice not found");
        return;
      }
      setModalShareInvoice(fullInvoice);
    } catch (err: any) {
      showErrorToast("Failed to fetch invoice details");
    }
  };

  const handleOfficialShare = async () => {
    if (!modalShareInvoice) return;
    setSharingInvoiceId(modalShareInvoice.id);
    try {
      setActiveInvoiceForShare(modalShareInvoice);

      // Wait a moment to ensure hidden print component is fully rendered
      await new Promise((resolve) => setTimeout(resolve, 600));

      if (sharePrintRef.current) {
        const blob = await exportElementAsPDF(sharePrintRef.current);
        const filename = `Invoice_${modalShareInvoice.invoice_no}.pdf`;
        const result = await shareInvoiceWhatsAppAPI(modalShareInvoice.id, blob, filename);
        showSuccessToast(`Invoice sent to ${result.sent_to || modalShareInvoice.customer_phone} on WhatsApp 🎉`);
      } else {
        showErrorToast("Print template not ready. Try again.");
      }
    } catch (err: any) {
      showErrorToast(err?.message || "Failed to share on WhatsApp.");
    } finally {
      setSharingInvoiceId(null);
      setActiveInvoiceForShare(null);
      setModalShareInvoice(null);
    }
  };

  const fetchInvoices = () => {
    dispatch(getAllInvoicesAPI({ page: currentPage, page_size: entries, search: searchText, branch_id: selectedBranch?.id }));
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchText(searchVal);
      setCurrentPage(1);
    }, 800);
    return () => clearTimeout(handler);
  }, [searchVal]);

  useEffect(() => {
    fetchInvoices();
  }, [dispatch, currentPage, entries, searchText, selectedBranch?.id]);

  const handleDelete = (id: string | number, no: string) => {
    setDeleteConfirm({ id, no });
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      setDeleting(true);
      await deleteInvoiceAPI(deleteConfirm.id);
      showSuccessToast("Invoice deleted successfully");
      fetchInvoices();
    } catch (err) {
      showErrorToast("Failed to delete invoice");
    } finally {
      setDeleting(false);
      setDeleteConfirm(null);
    }
  };

  const handleCopy = (e: React.MouseEvent) => {
    e.preventDefault();
    copyDataToClipboard(
      allInvoices,
      ["Invoice No", "Date", "Customer", "Total", "Balance", "Status", "Payment"],
      ["invoice_no", "invoice_date", "customer_name", "grand_total", "balance_due", "invoice_status", "payment_status"],
      "Invoices copied to clipboard"
    );
  };

  const handleExportCSV = (e: React.MouseEvent) => {
    e.preventDefault();
    exportDataToCSV(
      allInvoices,
      ["Invoice No", "Date", "Customer", "Total", "Balance", "Status", "Payment"],
      ["invoice_no", "invoice_date", "customer_name", "grand_total", "balance_due", "invoice_status", "payment_status"],
      "invoices_export.csv",
      "CSV exported successfully"
    );
  };

  const handleExportExcel = (e: React.MouseEvent) => {
    e.preventDefault();
    exportDataToExcel(
      allInvoices,
      ["Invoice No", "Date", "Customer", "Total", "Balance", "Status", "Payment"],
      ["invoice_no", "invoice_date", "customer_name", "grand_total", "balance_due", "invoice_status", "payment_status"],
      "invoices_export.xls",
      "Excel exported successfully"
    );
  };

  const reportStatusStyles: Record<string, { bg: string; color: string }> = {
    paid: { bg: 'rgba(49,166,106,0.15)', color: '#2d8a55' },
    fully_paid: { bg: 'rgba(49,166,106,0.15)', color: '#2d8a55' },
    sent: { bg: 'rgba(49,166,106,0.15)', color: '#2d8a55' },
    shared: { bg: 'rgba(49,166,106,0.15)', color: '#2d8a55' },
    partially_paid: { bg: 'rgba(240,154,62,0.18)', color: '#c47a2a' },
    draft: { bg: 'rgba(240,154,62,0.18)', color: '#c47a2a' },
    not_paid: { bg: 'rgba(228,72,98,0.14)', color: '#c93a55' },
    unpaid: { bg: 'rgba(228,72,98,0.14)', color: '#c93a55' },
    cancelled: { bg: 'rgba(228,72,98,0.14)', color: '#c93a55' },
    overdue: { bg: 'rgba(228,72,98,0.14)', color: '#c93a55' },
  };

  const getReportStatusStyle = (status: string) =>
    reportStatusStyles[status] ||
    reportStatusStyles[status?.toLowerCase()] ||
    { bg: 'rgba(93,28,52,0.1)', color: '#5D1C34' };

  const invoiceReportRows = useMemo(
    () =>
      allInvoices.map((inv) => ({
        invoice_no: inv.invoice_no,
        invoice_date: inv.invoice_date,
        customer_name: inv.customer_name,
        grand_total: `₹${formatINR(inv.grand_total)}`,
        balance_due: `₹${formatINR(inv.remaining_amount ?? inv.balance_due)}`,
        invoice_status: INVOICE_STATUS_LABELS[inv.invoice_status] || inv.invoice_status,
        invoice_status_key: inv.invoice_status,
        payment_status: PAYMENT_STATUS_LABELS[inv.payment_status] || inv.payment_status,
        payment_status_key: inv.payment_status,
        balance_raw: Number(inv.remaining_amount ?? inv.balance_due) || 0,
        total_raw: Number(inv.grand_total) || 0,
      })),
    [allInvoices]
  );

  const invoiceReportSummary = useMemo(() => {
    const totalBilled = invoiceReportRows.reduce((sum, row) => sum + row.total_raw, 0);
    const totalOutstanding = invoiceReportRows.reduce((sum, row) => sum + row.balance_raw, 0);
    return [
      { label: 'Total Invoices', value: String(invoiceReportRows.length) },
      { label: 'Total Billed', value: `₹${formatINR(totalBilled)}` },
      { label: 'Total Outstanding', value: `₹${formatINR(totalOutstanding)}`, highlight: true },
    ];
  }, [invoiceReportRows]);

  const handleExportPDF = async (e: React.MouseEvent) => {
    e.preventDefault();
    try {
      await new Promise((resolve) => setTimeout(resolve, 300));

      if (!reportPrintRef.current) {
        showErrorToast("Report template not ready");
        return;
      }

      const blob = await exportElementAsPDF(reportPrintRef.current);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "invoices_report.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      showSuccessToast("PDF exported successfully");
    } catch (err) {
      showErrorToast("Failed to generate PDF");
    }
  };

  const totalPages = Math.ceil(totalCount / entries) || 1;
  const hasActiveSearch = !!searchText;

  const closeMenu = useCallback(() => {
    setActiveMenuId(null);
    setMenuCoords(null);
  }, []);

  const handleToggleMenu = useCallback(
    (id: string | number) => {
      if (activeMenuId === id) {
        closeMenu();
        return;
      }

      const btn = menuButtonRefs.current[String(id)];
      if (!btn) {
        setActiveMenuId(id);
        return;
      }

      const btnRect = btn.getBoundingClientRect();
      const estimatedMenuHeight = 320;
      const gap = 8;
      const spaceBelow = window.innerHeight - btnRect.bottom;
      const spaceAbove = btnRect.top;
      const openUp = spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow;
      const right = Math.max(8, window.innerWidth - btnRect.right);

      setMenuCoords(
        openUp
          ? { bottom: window.innerHeight - btnRect.top + gap, right }
          : { top: btnRect.bottom + gap, right }
      );
      setActiveMenuId(id);
    },
    [activeMenuId, closeMenu]
  );

  useEffect(() => {
    if (activeMenuId === null) return;
    const scrollEl = scrollRef.current;
    scrollEl?.addEventListener("scroll", closeMenu, { passive: true });
    window.addEventListener("resize", closeMenu);
    return () => {
      scrollEl?.removeEventListener("scroll", closeMenu);
      window.removeEventListener("resize", closeMenu);
    };
  }, [activeMenuId, closeMenu]);

  const activeMenuInvoice = useMemo(
    () => allInvoices.find((inv) => inv.id === activeMenuId) ?? null,
    [allInvoices, activeMenuId]
  );

  return (
    <InvoicesPage>
      <Hero>
        <HeroGlow />
        <HeroContent>
          <HeroLeft>
            <BackChip type="button" onClick={() => navigate("/dashboard/")} aria-label="Go back">
              <ArrowLeft size={18} />
            </BackChip>
            <HeroText>
              <HeroBadge><ReceiptText size={11} /> Billing</HeroBadge>
              <PageTitle className="linear-text">All Invoices</PageTitle>
              <PageSubtitle>Manage billing, payments, and generated invoices.</PageSubtitle>
            </HeroText>
          </HeroLeft>
          <PermissionGate anyPermission={["invoice.create", "payments_management.create"]}>
            <SubmitButton
              type="button"
              $ready
              onClick={() => navigate("/dashboard/invoice/new")}
              style={{ minWidth: 180, height: 46 }}
            >
              <Plus size={16} /> Create Invoice
            </SubmitButton>
          </PermissionGate>
        </HeroContent>
      </Hero>

      <InvoicesPanel>
        <Toolbar>
          <ToolbarLeft>
            <SearchControl>
              <Search size={16} />
              <input
                value={searchVal}
                onChange={(e) => {
                  setSearchVal(e.target.value);
                }}
                placeholder="Search invoices"
              />
              {searchVal && (
                <ClearButton
                  type="button"
                  onClick={() => {
                    setSearchVal("");
                    setSearchText("");
                    setCurrentPage(1);
                  }}
                >
                  <X size={14} />
                </ClearButton>
              )}
            </SearchControl>

            <EntriesControl>
              <span>Show</span>
              <select value={entries} onChange={(e) => { setEntries(Number(e.target.value)); setCurrentPage(1); }}>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              <span>entries</span>
            </EntriesControl>
          </ToolbarLeft>

          <ToolbarRight>
            <TermsManageButton
              type="button"
              onClick={() => setIsStudioTermsModalOpen(true)}
              $hasTerms={hasStudioTerms}
              title={hasStudioTerms ? "Edit Studio Terms & Conditions" : "Add Studio Terms & Conditions"}
            >
              {hasStudioTerms ? <Pencil size={13} /> : <Plus size={13} />}
              <span>{hasStudioTerms ? "Edit Terms & Condition" : "Add Terms & Condition"}</span>
            </TermsManageButton>
            <ExportDropdown
              onCopy={handleCopy}
              onCSV={handleExportCSV}
              onExcel={handleExportExcel}
              onPDF={handleExportPDF}
              onPrint={() => window.print()}
            />
          </ToolbarRight>
        </Toolbar>

        <ListHeader style={{ padding: "0 22px" }}>
          <ListTitle>Invoice records</ListTitle>
          {!loading && (
            <CountBadge>
              {totalCount} record{totalCount !== 1 ? "s" : ""}
            </CountBadge>
          )}
        </ListHeader>

        {loading ? (
          <MarketplaceLoadingSpinner padding="60px 22px" />
        ) : allInvoices.length === 0 ? (
          <MarketplaceEmptyState
            padding="32px 22px 48px"
            icon={hasActiveSearch ? <SearchX size={36} strokeWidth={1.5} /> : <FileText size={36} strokeWidth={1.5} />}
            title={hasActiveSearch ? "No invoices found" : "No invoices yet"}
            description={
              hasActiveSearch
                ? "Nothing matched your search. Try a different invoice number, customer name, or keyword."
                : "Create your first invoice to start managing billing, payments, and customer statements."
            }
            actions={[
              ...(hasActiveSearch
                ? [{
                    label: "Clear search",
                    onClick: () => { setSearchVal(""); setSearchText(""); setCurrentPage(1); },
                    variant: "secondary" as const,
                  }]
                : []),
              ...(hasAnyPermission(["invoice.create", "payments_management.create"])
                ? [{
                    label: hasActiveSearch ? "Create Invoice" : "Create Your First Invoice",
                    onClick: () => navigate("/dashboard/invoice/new"),
                    icon: <Plus size={15} />,
                  }]
                : []),
            ]}
            hint={!hasActiveSearch ? {
              icon: <Sparkles size={13} />,
              text: "Share invoices, track payments & send payment links",
            } : undefined}
          />
        ) : (
          <InvoicesTableScroll ref={scrollRef}>
            <TableWrap>
              <PaymentsTable $minWidth={1000}>
              <thead>
                <tr>
                  <th style={{ width: 48 }}><span>ID</span></th>
                  <th><span>Invoice No</span></th>
                  <th><span>Date</span></th>
                  <th><span>Customer</span></th>
                  <th><span>Branch</span></th>
                  <th><span>Grand Total (₹)</span></th>
                  <th><span>Balance Due (₹)</span></th>
                  <th><span>Invoice sharing status</span></th>
                  <th><span>Payment</span></th>
                  <th style={{ textAlign: "right" }}><span>Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {allInvoices.map((inv, idx) => (
                  <tr key={inv.id}>
                    <td style={{ color: "rgba(23,23,23,0.45)", fontWeight: 600 }}>{(currentPage - 1) * entries + idx + 1}</td>
                    <td><InvoiceNo>{inv.invoice_no}</InvoiceNo></td>
                    <td style={{ whiteSpace: "nowrap" }}>{inv.invoice_date}</td>
                    <td style={{ whiteSpace: "nowrap", fontWeight: 600 }}>{inv.customer_name}</td>
                    <td>
                      {inv.branch_name ? (
                        <span style={{ fontSize: 11, background: '#e0e7ff', color: '#4338ca', padding: '2px 8px', borderRadius: 999, fontWeight: 600 }}>
                          {inv.branch_name}
                        </span>
                      ) : (
                        <span style={{ fontSize: 11, color: '#94a3b8' }}>-</span>
                      )}
                    </td>
                    <td><AmountCell>₹{formatINR(inv.grand_total)}</AmountCell></td>
                    <td>
                      {Number(inv.remaining_amount ?? inv.balance_due) > 0 ? (
                        <RemainingCell>₹{formatINR(inv.remaining_amount ?? inv.balance_due)}</RemainingCell>
                      ) : (
                        <AmountCell>₹{formatINR(inv.remaining_amount ?? inv.balance_due)}</AmountCell>
                      )}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <StatusBadge $status={INVOICE_STATUS_LABELS[inv.invoice_status] || inv.invoice_status}>
                        {INVOICE_STATUS_LABELS[inv.invoice_status] || inv.invoice_status}
                      </StatusBadge>
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <StatusBadge $status={inv.payment_status}>
                        {PAYMENT_STATUS_LABELS[inv.payment_status] || inv.payment_status}
                      </StatusBadge>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {allowedActionButtons.length > 0 && (
                        <MenuContainer>
                          <ThreeDotsButton
                            ref={(el) => { menuButtonRefs.current[String(inv.id)] = el; }}
                            type="button"
                            aria-expanded={activeMenuId === inv.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleMenu(inv.id);
                            }}
                          >
                            <Dot />
                            <Dot />
                            <Dot />
                          </ThreeDotsButton>
                        </MenuContainer>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              </PaymentsTable>
            </TableWrap>
          </InvoicesTableScroll>
        )}

        {!loading && allInvoices.length > 0 && <PanelDivider />}

        <FooterRow>
          <FooterText>
            Showing {totalCount === 0 ? 0 : (currentPage - 1) * entries + 1} to{" "}
            {Math.min(currentPage * entries, totalCount)} of {totalCount} entries
          </FooterText>
          <Pagination>
            <PageButton disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)}>Previous</PageButton>
            {Array.from({ length: totalPages }).map((_, idx) => (
              <PageButton key={idx} $active={currentPage === idx + 1} onClick={() => setCurrentPage(idx + 1)}>{idx + 1}</PageButton>
            ))}
            <PageButton disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => p + 1)}>Next</PageButton>
          </Pagination>
        </FooterRow>
      </InvoicesPanel>

      {activeMenuInvoice && menuCoords &&
        createPortal(
          <>
            <Backdrop onClick={closeMenu} />
            <DropdownMenu
              $fixed
              style={{
                top: menuCoords.top,
                bottom: menuCoords.bottom,
                right: menuCoords.right,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {allowedActionButtons.map((btn) => {
                const Icon = btn.icon;
                return (
                  <MenuItem
                    key={btn.key}
                    type="button"
                    disabled={sharingInvoiceId !== null}
                    onClick={() => {
                      closeMenu();
                      if (btn.key === "delete") handleDelete(activeMenuInvoice.id, activeMenuInvoice.invoice_no);
                      else if (btn.key === "edit") navigate(`/dashboard/invoice/edit?id=${activeMenuInvoice.id}`);
                      else if (btn.key === "view") navigate(`/dashboard/invoice/view?id=${activeMenuInvoice.id}`);
                      else if (btn.key === "share") handleShareInvoice(activeMenuInvoice.id);
                      else if (btn.key === "payment") navigate(`/dashboard/invoice/view?id=${activeMenuInvoice.id}&tab=payments`);
                      else if (btn.key === "paymentLink") handleSharePaymentLink(activeMenuInvoice);
                    }}
                  >
                    <ItemIconContainer>
                      {(sharingInvoiceId === activeMenuInvoice.id && btn.key === "share") || (fetchingInvoiceForLink === activeMenuInvoice.id && btn.key === "paymentLink") ? (
                        <Loader size={14} className="spin" />
                      ) : (
                        <Icon size={14} />
                      )}
                    </ItemIconContainer>
                    <ItemLabel>
                      {sharingInvoiceId === activeMenuInvoice.id && btn.key === "share"
                        ? "Sharing..."
                        : fetchingInvoiceForLink === activeMenuInvoice.id && btn.key === "paymentLink"
                          ? "Loading..."
                          : btn.label}
                    </ItemLabel>
                  </MenuItem>
                );
              })}
            </DropdownMenu>
          </>,
          document.body
        )}

      {deleteConfirm !== null && (
        <DeletePopup
          text={`Are you sure you want to delete invoice ${deleteConfirm.no}?`}
          buttonText={deleting ? "Deleting..." : "Delete"}
          cancel={() => setDeleteConfirm(null)}
          Delete={handleConfirmDelete}
        />
      )}

      {modalShareInvoice && (
        <InvoiceShareModal
          invoice={modalShareInvoice}
          onClose={() => setModalShareInvoice(null)}
          onShareOfficial={handleOfficialShare}
          loading={sharingInvoiceId === modalShareInvoice.id}
        />
      )}

      {activeInvoiceForShare && (
        <HiddenPrintContainer>
          <DocumentPrintTemplate
            type="invoice"
            activeDoc={activeInvoiceForShare}
            user={user}
            studioLogo={studio_logo || business?.studio_logo}
            studioName={business?.studio_name}
            studioAddress={business?.address}
            printRef={sharePrintRef}
          />
        </HiddenPrintContainer>
      )}

      <ListReportPrintTemplate
        printRef={reportPrintRef}
        title="Invoices Report"
        subtitle="Billing summary and payment overview"
        user={user ? { name: user.name, phone_number: user.phone_number, email: user.email } : undefined}
        columns={[
          { key: 'invoice_no', label: 'Invoice No', width: '14%' },
          { key: 'invoice_date', label: 'Date', width: '12%' },
          { key: 'customer_name', label: 'Customer', width: '22%' },
          { key: 'grand_total', label: 'Grand Total', align: 'right', width: '14%' },
          {
            key: 'balance_due',
            label: 'Balance Due',
            align: 'right',
            width: '14%',
            render: (value, row) => (
              <CellBold style={{ color: Number(row.balance_raw) > 0 ? '#c93a55' : '#2d8a55' }}>
                {String(value)}
              </CellBold>
            ),
          },
          {
            key: 'invoice_status',
            label: 'Status',
            align: 'center',
            width: '12%',
            render: (value, row) => {
              const style = getReportStatusStyle(String(row.invoice_status_key || ''));
              return (
                <ReportStatusBadge $bg={style.bg} $color={style.color}>
                  {String(value)}
                </ReportStatusBadge>
              );
            },
          },
          {
            key: 'payment_status',
            label: 'Payment',
            align: 'center',
            width: '12%',
            render: (value, row) => {
              const style = getReportStatusStyle(String(row.payment_status_key || ''));
              return (
                <ReportStatusBadge $bg={style.bg} $color={style.color}>
                  {String(value)}
                </ReportStatusBadge>
              );
            },
          },
        ]}
        rows={invoiceReportRows}
        summaryItems={invoiceReportSummary}
      />

      {paymentLinkInstallment !== null && paymentLinkInvoiceId !== null && (
        <PaymentLinkModal
          invoiceId={paymentLinkInvoiceId}
          installment={paymentLinkInstallment}
          onClose={() => {
            setPaymentLinkInstallment(null);
            setPaymentLinkInvoiceId(null);
          }}
          onSuccess={() => {
            fetchInvoices();
          }}
        />
      )}

      <StudioTermsManageModal
        isOpen={isStudioTermsModalOpen}
        onClose={() => setIsStudioTermsModalOpen(false)}
      />
    </InvoicesPage>
  );
};

export default AllInvoicesPage;

// ─── Styled Components ─────────────────────────

const PanelDivider = styled.div`
  height: 1px;
  background: rgba(23, 23, 23, 0.06);
  margin: 0 22px;
  flex-shrink: 0;
`;

const statusColors: Record<string, { bg: string; color: string }> = {
  paid: { bg: "rgba(49,166,106,0.12)", color: "#31a66a" },
  fully_paid: { bg: "rgba(49,166,106,0.12)", color: "#31a66a" },
  sent: { bg: "rgba(49,166,106,0.12)", color: "#31a66a" },
  shared: { bg: "rgba(49,166,106,0.12)", color: "#31a66a" },
  Shared: { bg: "rgba(49,166,106,0.12)", color: "#31a66a" },
  Active: { bg: "rgba(49,166,106,0.12)", color: "#31a66a" },
  Completed: { bg: "rgba(53,183,200,0.12)", color: "#35b7c8" },
  partially_paid: { bg: "rgba(240,154,62,0.15)", color: "#f09a3e" },
  draft: { bg: "rgba(240,154,62,0.15)", color: "#f09a3e" },
  "remaining to share": { bg: "rgba(240,154,62,0.15)", color: "#f09a3e" },
  "Remaining to share": { bg: "rgba(240,154,62,0.15)", color: "#f09a3e" },
  Pending: { bg: "rgba(240,154,62,0.15)", color: "#f09a3e" },
  not_paid: { bg: "rgba(228,72,98,0.12)", color: "#e44862" },
  unpaid: { bg: "rgba(228,72,98,0.12)", color: "#e44862" },
  cancelled: { bg: "rgba(228,72,98,0.12)", color: "#e44862" },
  overdue: { bg: "rgba(228,72,98,0.12)", color: "#e44862" },
  Cancelled: { bg: "rgba(228,72,98,0.12)", color: "#e44862" },
};

const StatusBadge = styled.span<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 12px;
  border-radius: 20px;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  background: ${(p) => statusColors[p.$status]?.bg ?? (statusColors[p.$status?.toLowerCase()]?.bg ?? "rgba(23,23,23,0.08)")};
  color:      ${(p) => statusColors[p.$status]?.color ?? (statusColors[p.$status?.toLowerCase()]?.color ?? "#171717")};
`;

const InvoiceNo = styled.span`
  font-weight: 700;
  white-space: nowrap;
`;

const HiddenPrintContainer = styled.div`
  position: absolute;
  top: -9999px;
  left: -9999px;
  width: 800px;
  height: auto;
  overflow: hidden;
  background: white;
  pointer-events: none;
`;

const TermsManageButton = styled.button<{ $hasTerms?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 36px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid ${({ $hasTerms }) => ($hasTerms ? "rgba(201, 77, 170, 0.3)" : "rgba(23, 23, 23, 0.12)")};
  background: ${({ $hasTerms }) => ($hasTerms ? "rgba(201, 77, 170, 0.05)" : "#ffffff")};
  color: ${({ $hasTerms }) => ($hasTerms ? "#c94daa" : "#171717")};
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
  transition: all 0.15s ease;

  &:hover {
    background: ${({ $hasTerms }) => ($hasTerms ? "rgba(201, 77, 170, 0.1)" : "#f8f9fa")};
    border-color: ${({ $hasTerms }) => ($hasTerms ? "#c94daa" : "rgba(23, 23, 23, 0.2)")};
    transform: translateY(-1px);
  }
`;
